import '@data/Simulations/pipeline/blocks/CloseToTerrainBlock';
import type { CloseToTerrainBlockParams } from '@data/Simulations/pipeline/blocks/CloseToTerrainBlock';
import { ControllerManager } from '@gui/controller/ControllerManager';
import RangeSlider from '@gui/components/generic/RangeSider';
import { Box } from '@mui/material';
import type { BlockParameters } from '@visualizer/types';
import Grid from 'Geodata/grid';
import type { FC } from 'react';
import { useEffect, useMemo } from 'react';
import { usePipelineBlockParams } from './usePipelineBlockParams';

type CloseToTerrainParametersProps = {
    blockId: string;
};

const getGridResolution = (): number => {
    const gridRes = Number.isFinite(Grid.gridRes) ? Grid.gridRes : 1;
    return gridRes > 0 ? gridRes : 1;
};

const getMaxRange = (gridRes: number): number => {
    const floors = Number.isFinite(Grid.maxFloor) ? Grid.maxFloor + 1 : (Grid.gridZ ?? 0) + 1;
    return gridRes * Math.max(1, floors);
};

const getDefaultRange = (): [number, number] => {
    const gridRes = getGridResolution();
    return [0, gridRes] as [number, number];
};

export const getCloseToTerrainDefaultParams = (): Pick<CloseToTerrainBlockParams, 'minValue' | 'maxValue'> => {
    const [minValue, maxValue] = getDefaultRange();
    return { minValue, maxValue };
};

const CloseToTerrainParameters: FC<CloseToTerrainParametersProps> = ({ blockId }) => {
    const params = usePipelineBlockParams<CloseToTerrainBlockParams>(blockId);
    const pipelineController = useMemo(() => ControllerManager.getInstance().getPipelineController(), []);

    const updateParams = (partial: Partial<CloseToTerrainBlockParams>) => {
        pipelineController.updateBlockParams(blockId, partial as BlockParameters);
    };

    const gridRes = getGridResolution();
    const defaultRange = getDefaultRange();
    const maxRange = getMaxRange(gridRes);

    useEffect(() => {
        if (!params) {
            updateParams({
                minValue: defaultRange[0],
                maxValue: defaultRange[1],
            });
            return;
        }

        const missing: Partial<CloseToTerrainBlockParams> = {};
        if (params.minValue == null) {
            missing.minValue = defaultRange[0];
        }
        if (params.maxValue == null) {
            missing.maxValue = defaultRange[1];
        }

        if (Object.keys(missing).length > 0) {
            updateParams(missing);
        }
    }, [params]);

    const currentRange: [number, number] = [params?.minValue ?? defaultRange[0], params?.maxValue ?? defaultRange[1]];

    const handleSliderChange = (next: [number, number]) => {
        const [minValue, maxValue] = next;
        updateParams({ minValue, maxValue });
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', pr: 1.7 }}>
            <RangeSlider
                value={currentRange}
                lastIndex={maxRange}
                showValues
                size="small"
                step={gridRes}
                onChange={handleSliderChange}
                sx={{ ml: '5px', mr: '10px', flex: 1, minWidth: 0 }}
            />
            <Box>[m]</Box>
        </Box>
    );
};

export default CloseToTerrainParameters;
